import React, { useState } from "react";
import { motion } from "framer-motion";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { services, industries, serviceAreas, articles, faqs, caseStudies } from "@/lib/data";
import { FaqSection, FinalCTA } from "@/components/marketing/Sections";
import SupportPlansSection from "@/components/marketing/SupportPlansSection";
import Breadcrumbs from "@/components/marketing/Breadcrumbs";
import SEO, { breadcrumbLd, faqLd } from "@/components/SEO";
import { Link } from "react-router-dom";
import { ArrowUpRight, Upload } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

const projectTypes = ["New opening", "Renovation", "Upgrade", "Troubleshooting"];
const budgetRanges = ["Under $5,000", "$5,000–$15,000", "$15,000–$50,000", "$50,000–$150,000", "$150,000+"];
const contactMethods = ["Email", "Phone", "Either"];

export function ContactPage() {
    const [form, setForm] = useState({
        name: "", company: "", job_title: "", email: "", phone: "", website: "",
        business_type: "", number_of_locations: "", city: "", project_type: "",
        target_completion_date: "", budget_range: "", services_needed: [],
        project_description: "", preferred_contact: "Email", heard_about_us: "",
        file_names: [], consent: false, honeypot: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
    const toggleService = (name) => setForm((f) => ({
        ...f,
        services_needed: f.services_needed.includes(name) ? f.services_needed.filter((n) => n !== name) : [...f.services_needed, name],
    }));

    const onFile = (e) => {
        const names = Array.from(e.target.files || []).map((f) => f.name);
        setForm((f) => ({ ...f, file_names: names }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.consent) { toast.error("Please accept the consent checkbox."); return; }
        setSubmitting(true);
        try {
            await api.post("/leads/consultation", form);
            setSuccess(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setSubmitting(false); }
    };

    if (success) {
        return (
            <MarketingLayout>
                <SEO title="Request received · Final Cut Technologies" description="Thanks for reaching out to Final Cut Technologies." />
                <section className="pt-[160px] pb-32 border-b border-line">
                    <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
                        <p className="eyebrow mb-6">Received</p>
                        <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95] mb-6">Thanks — request received.</h1>
                        <p className="text-muted text-lg mb-4">We'll review your request and reply within one business day, Monday to Friday.</p>
                        <p className="text-muted text-sm mb-10">If your issue is urgent, use the support form so we can triage properly.</p>
                        <div className="flex flex-wrap gap-3 justify-center">
                            <Link to="/" className="inline-flex items-center gap-2 bg-ink text-bg px-6 py-4 text-sm hover:bg-accent transition-colors">Back to home</Link>
                            <Link to="/services" className="inline-flex items-center gap-2 border border-ink text-ink px-6 py-4 text-sm">Browse services</Link>
                            <Link to="/support" className="inline-flex items-center gap-2 border border-ink text-ink px-6 py-4 text-sm">Urgent support</Link>
                        </div>
                    </div>
                </section>
            </MarketingLayout>
        );
    }

    return (
        <MarketingLayout>
            <SEO
                title="Request a Site Assessment · Final Cut Technologies"
                description="Book a commercial technology site assessment in Metro Vancouver. Share your space, timeline and scope — we'll reply within one business day."
            />
            <Toaster position="top-right" />
            <section className="pt-[132px] pb-24">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Contact", href: "/contact" }]} />
                        <p className="eyebrow mb-6">Request a Site Assessment</p>
                        <h1 className="font-display text-4xl md:text-6xl tracking-tight leading-[0.95] mb-6">Tell us about your space.</h1>
                        <p className="text-muted leading-relaxed mb-8">Only <span className="text-ink">Name</span> and <span className="text-ink">Email</span> are required — share whatever else is useful. We'll follow up to schedule a walkthrough or discovery call and confirm the next practical step.</p>
                        <div className="border-t border-line pt-6 space-y-4 text-sm">
                            <p className="text-muted"><span className="text-ink font-medium block">Call directly</span><a href="tel:+16046714247" className="text-gold font-mono tracking-wider hover:text-ink transition-colors">604-671-4247</a></p>
                            <p className="text-muted"><span className="text-ink font-medium block">Serving</span>Metro Vancouver &amp; the Lower Mainland.</p>
                            <p className="text-muted"><span className="text-ink font-medium block">Response</span>Within one business day, Mon–Fri.</p>
                            <p className="text-muted"><span className="text-ink font-medium block">Urgent?</span><Link to="/support" className="link-underline text-ink">Use the support form →</Link></p>
                            <p className="text-muted"><span className="text-ink font-medium block">Prefer email or phone?</span>Both work — tell us in the form.</p>
                        </div>
                    </div>

                    <form onSubmit={submit} className="md:col-span-7 space-y-6" data-testid="consultation-form">
                        {/* honeypot */}
                        <input type="text" name="website_hp" value={form.honeypot} onChange={(e) => setForm((f) => ({ ...f, honeypot: e.target.value }))} className="hidden" tabIndex="-1" autoComplete="off" />

                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label="Full name" required value={form.name} onChange={handle("name")} testid="input-name" />
                            <Field label="Company" value={form.company} onChange={handle("company")} testid="input-company" />
                            <Field label="Job title" value={form.job_title} onChange={handle("job_title")} testid="input-job" />
                            <Field label="Email" required type="email" value={form.email} onChange={handle("email")} testid="input-email" />
                            <Field label="Phone" value={form.phone} onChange={handle("phone")} testid="input-phone" />
                            <Field label="Website" value={form.website} onChange={handle("website")} testid="input-website" />
                            <Field label="Business type" placeholder="Restaurant, clinic, retail…" value={form.business_type} onChange={handle("business_type")} testid="input-business-type" />
                            <Field label="Number of locations" value={form.number_of_locations} onChange={handle("number_of_locations")} testid="input-locations" />
                            <Field label="City" value={form.city} onChange={handle("city")} testid="input-city" />
                            <SelectField label="Project type" value={form.project_type} onChange={handle("project_type")} options={projectTypes} testid="input-project-type" />
                            <Field label="Target completion date" type="date" value={form.target_completion_date} onChange={handle("target_completion_date")} testid="input-date" />
                            <SelectField label="Budget range" value={form.budget_range} onChange={handle("budget_range")} options={budgetRanges} testid="input-budget" />
                        </div>

                        <div>
                            <label className="eyebrow block mb-3">Services needed</label>
                            <div className="grid grid-cols-2 gap-2">
                                {services.map((s) => (
                                    <label key={s.slug} className="flex items-center gap-2 text-sm cursor-pointer hover:text-accent">
                                        <input type="checkbox" checked={form.services_needed.includes(s.title)} onChange={() => toggleService(s.title)} data-testid={`svc-${s.slug}`} className="accent-accent" />
                                        {s.title}
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="eyebrow block mb-2">Project description</label>
                            <textarea rows="4" value={form.project_description} onChange={handle("project_description")}
                                data-testid="input-description"
                                className="w-full border border-line bg-bg px-4 py-3 text-sm focus:border-ink focus:outline-none transition-colors" />
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            <SelectField label="Preferred contact method" value={form.preferred_contact} onChange={handle("preferred_contact")} options={contactMethods} testid="input-contact-method" />
                            <Field label="How did you hear about us?" value={form.heard_about_us} onChange={handle("heard_about_us")} testid="input-heard" />
                        </div>

                        <div>
                            <label className="eyebrow block mb-2">Photos, floor plans or equipment lists (optional)</label>
                            <label className="flex items-center gap-3 border border-dashed border-line bg-bg-alt px-4 py-4 text-sm cursor-pointer hover:border-ink">
                                <Upload className="w-4 h-4" />
                                <span>{form.file_names.length ? `${form.file_names.length} file(s) selected` : "Attach files"}</span>
                                <input type="file" multiple onChange={onFile} className="hidden" data-testid="input-files" />
                            </label>
                        </div>

                        <label className="flex items-start gap-3 text-sm text-muted">
                            <Checkbox data-testid="input-consent" checked={form.consent} onCheckedChange={(v) => setForm((f) => ({ ...f, consent: !!v }))} className="mt-1" />
                            <span>I consent to be contacted about my request. Final Cut Technologies will not share my information without permission.</span>
                        </label>

                        <button type="submit" disabled={submitting} data-testid="submit-consultation"
                            className="w-full inline-flex items-center justify-center gap-2 bg-ink text-bg px-6 py-4 text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50">
                            {submitting ? "Sending…" : "Send project brief"} <ArrowUpRight className="w-4 h-4" />
                        </button>
                        <p className="text-xs text-muted text-center">By submitting you consent to be contacted about this request. We don't share your information.</p>
                    </form>
                </div>
            </section>
        </MarketingLayout>
    );
}

function Field({ label, required, type = "text", value, onChange, placeholder, testid }) {
    return (
        <div>
            <label className="eyebrow block mb-2">{label}{required && " *"}</label>
            <input type={type} value={value} onChange={onChange} required={required} placeholder={placeholder} data-testid={testid}
                className="w-full border border-line bg-bg px-4 py-3 text-sm focus:border-ink focus:outline-none transition-colors" />
        </div>
    );
}

function SelectField({ label, value, onChange, options, testid }) {
    return (
        <div>
            <label className="eyebrow block mb-2">{label}</label>
            <select value={value} onChange={onChange} data-testid={testid}
                className="w-full border border-line bg-bg px-4 py-3 text-sm focus:border-ink focus:outline-none transition-colors appearance-none">
                <option value="">Select…</option>
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}

export function SupportPage() {
    const [form, setForm] = useState({
        company: "", contact_name: "", phone: "", email: "", location: "", system_affected: "",
        business_impact: "", issue_description: "", existing_plan_customer: false, best_access_time: "",
        file_names: [], honeypot: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try { await api.post("/leads/support", form); setSuccess(true); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
        finally { setSubmitting(false); }
    };

    return (
        <MarketingLayout>
            <SEO
                title="Urgent Commercial Technology Support · Metro Vancouver"
                description="Report an urgent commercial technology issue — Wi-Fi down, POS offline, digital signage frozen, audio out. We triage and confirm availability directly."
            />
            <Toaster position="top-right" />
            <section className="pt-[132px] pb-24">
                <div className="max-w-[900px] mx-auto px-6 md:px-10">
                    <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Support", href: "/support" }]} />
                    <p className="eyebrow mb-6">Urgent Support</p>
                    <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-[0.95] mb-4">Something down or acting up?</h1>
                    <div className="border border-line bg-bg-alt p-5 md:p-6 mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <p className="tick text-muted mb-1">Fastest way to reach us</p>
                            <a href="tel:+16046714247" className="font-display text-3xl md:text-4xl tracking-tight text-ink hover:text-gold transition-colors">604-671-4247</a>
                        </div>
                        <a href="tel:+16046714247" className="inline-flex items-center justify-center gap-2 bg-ink text-bg px-5 py-3 text-sm font-mono tracking-widest hover:bg-gold hover:text-ink transition-colors">Call now</a>
                    </div>
                    <p className="text-muted mb-2">Or fill out the form below — give us the essentials and we'll respond as quickly as we can.</p>
                    <p className="text-muted text-sm">Submitting this form doesn't guarantee emergency or same-day service — availability is confirmed directly.</p>

                    {success ? (
                        <div className="mt-10 border border-line p-8">
                            <h2 className="font-display text-3xl mb-3">Received.</h2>
                            <p className="text-muted">We'll respond to confirm availability. Thanks for the detail.</p>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="support-form">
                            <input type="text" value={form.honeypot} onChange={(e) => setForm((f) => ({ ...f, honeypot: e.target.value }))} className="hidden" tabIndex="-1" autoComplete="off" />
                            <div className="grid md:grid-cols-2 gap-4">
                                <Field label="Company" required value={form.company} onChange={handle("company")} testid="sup-company" />
                                <Field label="Contact name" required value={form.contact_name} onChange={handle("contact_name")} testid="sup-contact" />
                                <Field label="Phone" required value={form.phone} onChange={handle("phone")} testid="sup-phone" />
                                <Field label="Email" type="email" required value={form.email} onChange={handle("email")} testid="sup-email" />
                                <Field label="Location / address" value={form.location} onChange={handle("location")} testid="sup-location" />
                                <Field label="System affected" value={form.system_affected} onChange={handle("system_affected")} placeholder="Wi-Fi, POS, TV, audio…" testid="sup-system" />
                                <Field label="Business impact" value={form.business_impact} onChange={handle("business_impact")} placeholder="Blocking service? Slowing things?" testid="sup-impact" />
                                <Field label="Best access time" value={form.best_access_time} onChange={handle("best_access_time")} testid="sup-access" />
                            </div>
                            <div>
                                <label className="eyebrow block mb-2">Issue description *</label>
                                <textarea required rows="5" value={form.issue_description} onChange={handle("issue_description")} data-testid="sup-desc"
                                    className="w-full border border-line bg-bg px-4 py-3 text-sm focus:border-ink focus:outline-none" />
                            </div>
                            <label className="flex items-center gap-3 text-sm text-muted">
                                <input type="checkbox" checked={form.existing_plan_customer} onChange={(e) => setForm((f) => ({ ...f, existing_plan_customer: e.target.checked }))} data-testid="sup-existing" className="accent-accent" />
                                <span>I'm an existing support-plan customer.</span>
                            </label>
                            <button type="submit" disabled={submitting} data-testid="submit-support"
                                className="w-full inline-flex items-center justify-center gap-2 bg-ink text-bg px-6 py-4 text-sm hover:bg-accent transition-colors disabled:opacity-50">
                                {submitting ? "Sending…" : "Submit support request"} <ArrowUpRight className="w-4 h-4" />
                            </button>
                        </form>
                    )}
                </div>
            </section>
        </MarketingLayout>
    );
}

export function AboutPage() {
    return (
        <MarketingLayout>
            <SEO
                title="About Final Cut Technologies · Commercial Tech in Metro Vancouver"
                description="A commercial-only technology installation and support company for Metro Vancouver businesses — restaurants, hotels, clinics, retail and offices."
            />
            <section className="pt-[132px] pb-24 border-b border-line">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10">
                    <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "About", href: "/about" }]} />
                    <p className="eyebrow mb-6">About</p>
                    <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                        className="font-display text-5xl md:text-[92px] tracking-tight leading-[0.9] max-w-4xl">
                        The technology partner behind better-run businesses.
                    </motion.h1>
                    <div className="mt-14 grid md:grid-cols-12 gap-10">
                        <div className="md:col-span-7 space-y-6 text-ink/85 leading-relaxed text-lg">
                            <p>Final Cut Technologies is a commercial technology installation, integration and support company serving Metro Vancouver and the Lower Mainland, BC.</p>
                            <p>We install and organize the systems your business relies on every day — AV, business Wi-Fi, digital signage, POS hardware, commercial audio, cable management and preventive maintenance — under one accountable relationship.</p>
                            <p>We're not a residential TV installer, a computer repair shop or an enterprise IT consultancy. We're a specialist team that shows up prepared, coordinates cleanly and stays reachable after the job.</p>
                        </div>
                        <div className="md:col-span-5">
                            <div className="border border-line p-6 space-y-6">
                                <div>
                                    <p className="eyebrow mb-2">Focus</p>
                                    <p className="font-display text-2xl tracking-tight">Commercial only. Metro Vancouver.</p>
                                </div>
                                <div>
                                    <p className="eyebrow mb-2">How we operate</p>
                                    <p className="font-display text-2xl tracking-tight">Plan early. Coordinate vendors. Stay available.</p>
                                </div>
                                <div>
                                    <p className="eyebrow mb-2">Being finalized</p>
                                    <p className="text-sm text-muted">Team bios, insurance details, WorkSafeBC coverage, vendor partnerships and case studies will be published here as they're finalized — no invented content in the meantime.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <FinalCTA />
        </MarketingLayout>
    );
}

export function ServiceAreasPage() {
    return (
        <MarketingLayout>
            <SEO
                title="Service Areas · Commercial Technology across Metro Vancouver & the Lower Mainland"
                description="Commercial technology installation and support across Vancouver, Burnaby, Richmond, Surrey, Coquitlam and every corner of the Lower Mainland, British Columbia."
            />
            <section className="pt-[132px] pb-16 border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Service Areas", href: "/service-areas" }]} />
                    <p className="eyebrow mb-6">Service Areas</p>
                    <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95] max-w-4xl">Metro Vancouver &amp; the Lower Mainland.</h1>
                    <p className="mt-6 text-muted max-w-2xl">We work across the region. Local details on each city page are added deliberately — as real project references and specifics become available, rather than as duplicated boilerplate.</p>
                </div>
            </section>
            <section className="border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-line">
                    {serviceAreas.map((c, i) => (
                        <div key={c.city} className="border-r border-b border-line p-8 min-h-[220px] hover:bg-bg-alt transition-colors">
                            <span className="font-mono text-xs text-muted tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                            <h3 className="font-display text-3xl tracking-tight mt-4 mb-3">{c.city}</h3>
                            <p className="text-sm text-muted leading-relaxed">{c.description}</p>
                        </div>
                    ))}
                </div>
            </section>
            <FinalCTA />
        </MarketingLayout>
    );
}

export function ResourcesPage() {
    return (
        <MarketingLayout>
            <SEO
                title="Resources · Commercial Technology Field Notes"
                description="Practical articles on planning, budgeting and running the technology side of a Metro Vancouver commercial space."
            />
            <section className="pt-[132px] pb-16 border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Resources", href: "/resources" }]} />
                    <p className="eyebrow mb-6">Resources</p>
                    <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95] max-w-4xl">Field notes for business owners.</h1>
                    <p className="mt-6 text-muted max-w-2xl">Practical articles on planning, budgeting and running the technology side of a commercial space. First titles publish shortly.</p>
                </div>
            </section>
            <section>
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-2 border-t border-l border-line">
                    {articles.map((a, i) => (
                        <div key={a.slug} className="border-r border-b border-line p-10 hover:bg-bg-alt transition-colors">
                            <div className="flex items-center justify-between mb-6">
                                <span className="font-mono text-xs text-muted tracking-widest">{String(i + 1).padStart(2, "0")} · {a.category}</span>
                            </div>
                            <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-3">{a.title}</h3>
                            <p className="text-sm text-muted mb-6 leading-relaxed">{a.excerpt}</p>
                            <span className="tick uppercase text-muted">In development</span>
                        </div>
                    ))}
                </div>
            </section>
            <FinalCTA />
        </MarketingLayout>
    );
}

export function FaqPage() {
    return (
        <MarketingLayout>
            <SEO
                title="FAQ · Commercial Technology Installation & Support · Metro Vancouver"
                description="Answers to the questions businesses ask before starting a commercial technology project — service areas, pricing, contractor coordination, support plans and more."
                jsonLd={faqLd(faqs)}
            />
            <section className="pt-[132px] pb-8 border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "FAQ", href: "/faq" }]} />
                    <p className="eyebrow mb-6">Frequently asked</p>
                    <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95] max-w-4xl">Frequently asked.</h1>
                </div>
            </section>
            <FaqSection items={faqs} />
            <FinalCTA />
        </MarketingLayout>
    );
}

export function ProjectsPage() {
    return (
        <MarketingLayout>
            <SEO
                title="Projects · Commercial Technology Case Studies · Metro Vancouver"
                description="Selected commercial technology projects across Metro Vancouver — restaurants, hotels, clinics, retail and offices. Case studies with photography and client outcomes."
            />
            <section className="pt-[132px] pb-16 border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Projects", href: "/projects" }]} />
                    <p className="eyebrow mb-6"><span className="text-gold">●</span> Selected work</p>
                    <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95] max-w-4xl">Projects.</h1>
                    <p className="mt-6 text-muted max-w-2xl">Real commercial technology installations from Final Cut Technologies. New case studies publish here as photography and client approvals come in.</p>
                </div>
            </section>

            <section className="border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-20 grid gap-8">
                    {caseStudies.map((cs, i) => (
                        <Link key={cs.slug} to={`/projects/${cs.slug}`} className="group grid md:grid-cols-12 gap-0 border border-line bg-bg hover:bg-ink hover:text-bg transition-colors duration-500 overflow-hidden">
                            <div className="md:col-span-7 relative aspect-[16/10] overflow-hidden bg-ink">
                                <img src={cs.cover} alt={`${cs.client} — ${cs.title}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                                <span className="absolute top-4 left-4 bg-gold text-ink text-[10px] tracking-widest font-mono px-2 py-1 uppercase">Case Study · {String(i + 1).padStart(2, "0")}</span>
                            </div>
                            <div className="md:col-span-5 p-8 md:p-12 flex flex-col">
                                <p className="tick text-muted group-hover:text-bg/60 mb-6">{cs.industry} · {cs.location} · {cs.year}</p>
                                <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[0.95] mb-4">{cs.client}</h2>
                                <p className="text-sm text-muted group-hover:text-bg/70 mb-6">{cs.title}</p>
                                <p className="text-sm text-ink/80 group-hover:text-bg/85 leading-relaxed mb-8 flex-1">{cs.overview.slice(0, 210)}…</p>
                                <div className="flex items-center justify-between border-t border-line group-hover:border-bg/25 pt-6">
                                    <span className="text-sm">Read the case study</span>
                                    <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                                </div>
                            </div>
                        </Link>
                    ))}

                    {/* Coming soon */}
                    <div className="border border-dashed border-line p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div>
                            <p className="eyebrow mb-2">More projects</p>
                            <h3 className="font-display text-2xl md:text-3xl tracking-tight">Case studies in preparation.</h3>
                            <p className="text-muted text-sm mt-2 max-w-xl">Restaurants, clinics, boutique hotels and multi-location retailers — publishing as photography and approvals land.</p>
                        </div>
                        <Link to="/contact" className="inline-flex items-center gap-2 border border-ink text-ink px-5 py-3 text-sm hover:bg-ink hover:text-bg transition-colors">Discuss your project <ArrowUpRight className="w-4 h-4" /></Link>
                    </div>
                </div>
            </section>
            <FinalCTA />
        </MarketingLayout>
    );
}

export function SupportPlansPage() {
    return (
        <MarketingLayout>
            <SEO
                title="Managed Technology Support Plans · Metro Vancouver"
                description="Essential, Proactive and Multi-Location managed support plans for restaurants, hotels, clinics, retail and offices across Metro Vancouver."
            />
            <section className="pt-[132px] pb-8 border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Support Plans", href: "/support-plans" }]} />
                    <p className="eyebrow mb-6">Support Plans</p>
                    <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95] max-w-4xl">Recurring support, clearly scoped.</h1>
                    <p className="mt-6 text-muted max-w-2xl">Three plan structures with a shared principle: one point of contact, preventive checks and preferred scheduling. Custom pricing after a short assessment.</p>
                </div>
            </section>
            <SupportPlansSection />
            <FinalCTA />
        </MarketingLayout>
    );
}

export function PrivacyPage() {
    return (
        <MarketingLayout>
            <SEO title="Privacy · Final Cut Technologies" description="Privacy policy for Final Cut Technologies — Metro Vancouver commercial technology company." />
            <section className="pt-[132px] pb-24 border-b border-line">
                <div className="max-w-[900px] mx-auto px-6 md:px-10">
                    <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Privacy", href: "/privacy" }]} />
                    <p className="eyebrow mb-4">Privacy Policy — being finalized</p>
                    <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[0.95] mb-8">Privacy Policy</h1>
                    <p className="text-muted leading-relaxed">A finalized privacy policy covering the personal information we collect via the site, how it's used and how visitors can request updates will be published before public launch. In the meantime, information submitted through the site is used only to respond to your request.</p>
                </div>
            </section>
        </MarketingLayout>
    );
}

export function TermsPage() {
    return (
        <MarketingLayout>
            <SEO title="Terms of Use · Final Cut Technologies" description="Terms of use for the Final Cut Technologies website." />
            <section className="pt-[132px] pb-24 border-b border-line">
                <div className="max-w-[900px] mx-auto px-6 md:px-10">
                    <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Terms", href: "/terms" }]} />
                    <p className="eyebrow mb-4">Terms of Use — being finalized</p>
                    <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[0.95] mb-8">Terms of Use</h1>
                    <p className="text-muted leading-relaxed">Finalized Terms of Use governing this website and any related communications will be published prior to public launch.</p>
                </div>
            </section>
        </MarketingLayout>
    );
}

export function NotFoundPage() {
    return (
        <MarketingLayout>
            <SEO title="Page not found · Final Cut Technologies" description="The page you're looking for doesn't exist." />
            <section className="pt-[160px] pb-32 text-center">
                <div className="max-w-[900px] mx-auto px-6">
                    <p className="eyebrow mb-6">404</p>
                    <h1 className="font-display text-7xl md:text-9xl tracking-tight leading-[0.9] mb-6">Not here.</h1>
                    <p className="text-muted mb-10">The page you're looking for doesn't exist or has moved.</p>
                    <Link to="/" className="inline-flex items-center gap-2 bg-ink text-bg px-6 py-4 text-sm hover:bg-accent transition-colors">Back to home <ArrowUpRight className="w-4 h-4" /></Link>
                </div>
            </section>
        </MarketingLayout>
    );
}
